import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, Star } from 'lucide-react';
import { CHAPTERS } from '../mock';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', topic: 'General', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill out name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(`Thanks ${form.name.split(' ')[0]} — your message was sent. We will be in touch.`);
      setForm({ name: '', email: '', topic: 'General', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send your message. Please try again or email us directly.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fade-in-up">
      <section className="banner-bg section-noise">
        <div className="max-w-5xl mx-auto px-4 py-14 text-center">
          <div className="chip mb-3">REACH OUT</div>
          <h1 className="font-display text-5xl md:text-6xl text-cbtu-cream">GET <span className="gold-text-fill">INVOLVED</span></h1>
          <p className="text-cbtu-text/90 mt-3 max-w-2xl mx-auto">Whether you’re looking to join a chapter, partner with us, or send a press inquiry — we’re glad you’re here.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 card-dark p-6 rounded-md space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs tracking-widest text-gold font-heading mb-1">Full Name *</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs tracking-widest text-gold font-heading mb-1">Email *</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs tracking-widest text-gold font-heading mb-1">Topic</span>
            <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none">
              <option>General</option>
              <option>Join a Chapter</option>
              <option>2026 Conference</option>
              <option>Sponsorship</option>
              <option>Press / Media</option>
              <option>Volunteer</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs tracking-widest text-gold font-heading mb-1">Message *</span>
            <textarea rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none resize-none" />
          </label>
          <button type="submit" disabled={submitting} className="gold-button px-6 py-3 rounded-sm inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"><Send size={16} /> {submitting ? 'Sending…' : 'Send Message'}</button>
        </form>
        <aside className="space-y-4">
          <div className="card-dark p-6 rounded-md">
            <div className="font-heading tracking-widest text-gold text-xs mb-3">REGION III HEADQUARTERS</div>
            <ul className="space-y-2.5 text-sm text-cbtu-cream">
              <li className="flex items-start gap-2"><MapPin size={14} className="text-gold mt-0.5" /> P.O. Box 8303, Fort Lauderdale, FL 33310</li>
              <li className="flex items-start gap-2"><Star size={12} className="text-gold mt-1" fill="currentColor" /> <span>Region III Representative<br/><span className="text-cbtu-muted text-xs">Mike D. Payne — ILA</span></span></li>
              <li className="flex items-center gap-2"><Mail size={14} className="text-gold" /> <a href="mailto:cbturegioniii@gmail.com" className="hover:text-gold-bright">cbturegioniii@gmail.com</a></li>
            </ul>
          </div>
          <div className="card-dark p-6 rounded-md">
            <div className="font-heading tracking-widest text-gold text-xs mb-3">CHAPTER CONTACTS</div>
            <ul className="space-y-2 text-sm">
              {CHAPTERS.map((c) => (
                <li key={c.slug} className="flex items-start gap-2">
                  <Star size={10} className="text-gold mt-1.5 flex-shrink-0" fill="currentColor" />
                  <div>
                    <div className="text-cbtu-cream">{c.name}</div>
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="text-cbtu-muted text-xs hover:text-gold-bright">{c.email}</a>
                    ) : (
                      <span className="text-cbtu-muted text-xs italic">Contact info coming soon</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
